import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { Grid3x3, Save, Plus, Trash2, Loader2, Target, Settings2 } from 'lucide-react';

const ALL = '__all__';
const LEVEL_COLORS = ['#e5e7eb', '#fca5a5', '#fdba74', '#fde047', '#86efac'];

export default function SkillMatrixPage() {
  const { get, post, put, del } = useApi();
  const { hasPermission } = useAuth();
  const canConfig = hasPermission('skills.config');
  const canRate = hasPermission('skills.update');

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState(ALL);
  const [data, setData] = useState({ competencies: [], trainees: [], ratings: {}, gaps: [], level_labels: [] });
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [radarTrainee, setRadarTrainee] = useState('');

  // competency manager
  const [mgrOpen, setMgrOpen] = useState(false);
  const [comps, setComps] = useState([]);
  const [newComp, setNewComp] = useState({ name: '', category: '', target_level: 3 });

  useEffect(() => { get('/trainees/batches').then(r => setBatches(r.batches || [])).catch(() => {}); }, [get]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get(`/trainees/skills/matrix${batchId === ALL ? '' : `?batch_id=${batchId}`}`);
      setData(r); setDirty({});
      if (!radarTrainee && r.trainees?.length) setRadarTrainee(r.trainees[0].trainee_id);
      else if (r.trainees?.length) {
        const ids = r.trainees.map(t => t.trainee_id);
        setRadarTrainee(prev => (prev && ids.includes(prev)) ? prev : ids[0]);
      }
    } catch { toast.error('Failed to load skill matrix'); }
    finally { setLoading(false); }
  }, [get, batchId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const cellLevel = (tid, cid) => {
    const k = `${tid}:${cid}`;
    if (k in dirty) return dirty[k];
    return data.ratings?.[tid]?.[cid] ?? 0;
  };
  const setCell = (tid, cid, level) => setDirty(d => ({ ...d, [`${tid}:${cid}`]: Number(level) }));

  const save = async () => {
    const ratings = Object.entries(dirty).map(([k, level]) => {
      const [trainee_id, competency_id] = k.split(':');
      return { trainee_id, competency_id, level };
    });
    if (ratings.length === 0) { toast.message('No changes to save'); return; }
    setSaving(true);
    try { await post('/trainees/skills/rate', { ratings }); toast.success(`Saved ${ratings.length} rating(s)`); await load(); }
    catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const openMgr = async () => {
    setMgrOpen(true);
    try { const r = await get('/trainees/skills/competencies'); setComps(r.competencies || []); } catch {}
  };
  const addComp = async () => {
    if (!newComp.name.trim()) { toast.error('Name required'); return; }
    try {
      await post('/trainees/skills/competencies', { ...newComp, target_level: Number(newComp.target_level) });
      setNewComp({ name: '', category: '', target_level: 3 });
      await openMgr(); await load();
      toast.success('Competency added');
    } catch { toast.error('Add failed'); }
  };
  const removeComp = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and all its ratings?`)) return;
    try { await del(`/trainees/skills/competencies/${c.competency_id}`); await openMgr(); await load(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const radarData = data.competencies.map(c => ({
    competency: c.name,
    level: cellLevel(radarTrainee, c.competency_id),
    target: c.target_level,
  }));
  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="skill-matrix-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Grid3x3 className="w-5 h-5 text-amber-600" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Skill Matrix</h1>
            <p className="text-sm text-muted-foreground">Rate competencies (0–4) and spot capability gaps.</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canConfig && <Button variant="outline" onClick={openMgr} data-testid="skill-manage-btn"><Settings2 className="w-4 h-4 mr-2" />Competencies</Button>}
          {canRate && <Button onClick={save} disabled={saving || dirtyCount === 0} data-testid="skill-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save{dirtyCount ? ` (${dirtyCount})` : ''}
          </Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={batchId} onValueChange={setBatchId}>
          <SelectTrigger className="w-56" data-testid="skill-batch-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All batches</SelectItem>
            {batches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs">
          {(data.level_labels || []).map((lbl, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ background: LEVEL_COLORS[i] }} />{i}·{lbl}
            </span>
          ))}
        </div>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : data.competencies.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="skill-empty">
          No competencies defined yet.{canConfig ? ' Click "Competencies" to add some.' : ''}
        </Card>
      ) : (
        <>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left p-3 sticky left-0 bg-muted/40 min-w-[180px]">Trainee</th>
                  {data.competencies.map(c => (
                    <th key={c.competency_id} className="p-3 text-center font-medium min-w-[110px]" title={c.description || ''}>
                      {c.name}<div className="text-[10px] text-muted-foreground font-normal">target {c.target_level}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.trainees.length === 0 && <tr><td colSpan={data.competencies.length + 1} className="text-center text-muted-foreground py-6">No trainees in scope.</td></tr>}
                {data.trainees.map(t => (
                  <tr key={t.trainee_id} className="border-b last:border-0" data-testid={`skill-row-${t.trainee_id}`}>
                    <td className="p-2 sticky left-0 bg-card font-medium">{t.name}</td>
                    {data.competencies.map(c => {
                      const lv = cellLevel(t.trainee_id, c.competency_id);
                      return (
                        <td key={c.competency_id} className="p-1 text-center">
                          <select
                            value={lv}
                            disabled={!canRate}
                            onChange={(e) => setCell(t.trainee_id, c.competency_id, e.target.value)}
                            className="w-12 h-8 rounded text-center font-semibold border-0 cursor-pointer focus:ring-2 focus:ring-amber-400"
                            style={{ background: LEVEL_COLORS[lv], color: lv >= 1 ? '#1f2937' : '#9ca3af' }}
                            data-testid={`skill-cell-${t.trainee_id}-${c.competency_id}`}
                          >
                            {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4" data-testid="skill-gaps">
              <div className="font-display font-semibold mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-amber-600" />Competency gaps</div>
              <div className="space-y-2.5">
                {data.gaps.map(g => (
                  <div key={g.competency_id}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span>{g.name}</span>
                      <span className="text-muted-foreground">avg {g.avg_level} / target {g.target_level} · {g.assessed_count}/{g.total} rated</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(g.avg_level / 4) * 100}%`, background: g.gap > 1 ? '#ef4444' : g.gap > 0 ? '#f59e0b' : '#22c55e' }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-4" data-testid="skill-radar">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-semibold">Trainee profile</div>
                <Select value={radarTrainee} onValueChange={setRadarTrainee}>
                  <SelectTrigger className="w-44 h-8" data-testid="skill-radar-trainee"><SelectValue placeholder="Pick trainee" /></SelectTrigger>
                  <SelectContent>{data.trainees.map(t => <SelectItem key={t.trainee_id} value={t.trainee_id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="competency" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 4]} tick={{ fontSize: 10 }} />
                  <Radar name="Level" dataKey="level" stroke="#d97706" fill="#f59e0b" fillOpacity={0.5} />
                  <Radar name="Target" dataKey="target" stroke="#64748b" fill="#94a3b8" fillOpacity={0.1} />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      )}

      {/* Competency manager */}
      <Dialog open={mgrOpen} onOpenChange={setMgrOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="skill-mgr-dialog">
          <DialogHeader><DialogTitle>Competencies</DialogTitle>
            <DialogDescription className="sr-only">Define competencies and their target proficiency level.</DialogDescription></DialogHeader>
          <div className="space-y-2 mb-4">
            {comps.length === 0 && <p className="text-sm text-muted-foreground">No competencies yet.</p>}
            {comps.map(c => (
              <div key={c.competency_id} className="flex items-center justify-between rounded border p-2" data-testid={`comp-row-${c.competency_id}`}>
                <div><span className="font-medium text-sm">{c.name}</span>{c.category && <span className="text-xs text-muted-foreground ml-2">{c.category}</span>}</div>
                <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">target {c.target_level}</span>
                  <Button size="icon" variant="ghost" className="text-rose-600 h-7 w-7" onClick={() => removeComp(c)}><Trash2 className="w-3.5 h-3.5" /></Button></div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border p-3 space-y-2 bg-muted/20">
            <Label className="text-xs">Add competency</Label>
            <div className="flex gap-2">
              <Input value={newComp.name} onChange={(e) => setNewComp(n => ({ ...n, name: e.target.value }))} placeholder="Name" data-testid="comp-name-input" />
              <Input value={newComp.category} onChange={(e) => setNewComp(n => ({ ...n, category: e.target.value }))} placeholder="Category" className="w-32" />
              <Select value={String(newComp.target_level)} onValueChange={(v) => setNewComp(n => ({ ...n, target_level: v }))}>
                <SelectTrigger className="w-28" data-testid="comp-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)}>Target {n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={addComp} data-testid="comp-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMgrOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
